import { DOCUMENT, Injectable, computed, inject, signal } from '@angular/core';
import { CurrencyService } from './currency.service';
import { CountryService } from './country.service';
import { TranslationService } from './translation/translation.service';
import { BrandConfigService } from './brand-config/brand-config.service';
import { first, forkJoin, switchMap, tap } from 'rxjs';
import { PlatformService } from 'ngx-unificator/services';
import { CountryStateService } from './country-state.service';
import { EnvironmentService } from './environment.service';
import { WA_WINDOW } from 'ngx-unificator/tokens';

@Injectable({
  providedIn: 'root',
})
export class DataPreloaderService {
  private _currency = inject(CurrencyService);
  private _country = inject(CountryService);
  private _countryState = inject(CountryStateService);
  private _translate = inject(TranslationService);
  private _brandConfig = inject(BrandConfigService);
  private _env = inject(EnvironmentService);
  private _document = inject(DOCUMENT);
  private _platform = inject(PlatformService);
  private _window = inject(WA_WINDOW);

  private _countryStatesLoaded = signal(false);

  /**
   * A computed property that indicates whether all critical data has been loaded.
   * This includes the loading of countries, translations, and currencies.
   * @returns {boolean} `true` if all critical data has been loaded, `false` otherwise.
   */
  public criticalDataLoaded = computed(() => this._currency.loaded() && this._country.loaded() && this._countryStatesLoaded() && this._translate.loaded());

  /**
   * An array of critical data that needs to be preloaded before the application can fully function.
   * This includes loading countries, translations, and currencies.
   */
  private _criticalDataToPreload(): any[] {
    return [
      this._currency.loadCurrencies(),
      this._country.loadCountries(),
      this._countryState.loadCountryStates(this._env.dataEnv()?.country?.short).pipe(tap(() => this._countryStatesLoaded.set(true))),
      this._translate.loadTranslates(),
    ];
  }

  /**
   * An array of third-party data that needs to be preloaded before the application can fully function.
   * This may include data from external services or APIs that the application depends on.
   */
  private _thirdPartyDataToPreload(): any[] {
    return [];
  }

  /**
   * The `preloadCriticalData` function preloads critical data by resolving language, fetching brand
   * configuration, and preloading necessary data.
   */
  public preloadCriticalData() {
    this._brandConfig.brandConfigResolved$
      .pipe(
        switchMap(() => forkJoin([...this._criticalDataToPreload(), ...this._thirdPartyDataToPreload()])),
        first(),
      )
      .subscribe(() => {
        this._destroyLoader();
      });
  }

  /**
   * The function `_destroyLoader` removes the browser loader element from the document if it exists.
   */
  private _destroyLoader() {
    const loader = this._document?.getElementById('browser-loader');

    if (!this._platform.isBrowser) {
      loader?.remove();
    }

    if (this._platform.isBrowser && !loader) {
      this._window['__APP_READY__'] = true; // for ssr + js
    }
    if (this._platform.isBrowser && loader) {
      loader?.remove();
      this._window['__APP_READY__'] = true; // for spa
    }
  }
}
